/**
 * WHICH app account a QuickBooks-sourced row belongs to — one rule for every importer
 * that has to answer it (#645): the IIF customer import (api/customers-bulk.ts), the
 * journal CSV import (api/accounting/import-journal-csv.ts), the AR enrichment matcher
 * (api/accounting/qb-enrichment-match.ts) and the starting-balances import
 * (components/accounting/import-starting-balances-modal.tsx).
 *
 * Precedence, strongest identity first:
 *   1. external_id   — the IIF REFNUM, QuickBooks' stable per-list customer id.
 *   2. external_name — the QB customer name as last imported (flag tokens stripped,
 *                      case/space-insensitive). Ignored when two accounts share it, or
 *                      when both sides carry QB ids that differ.
 *   3. number        — the unit number, ONLY when the account's stored owner agrees with
 *                      the incoming name. When they disagree it is a CONFLICT: the row is
 *                      skipped and reported, never posted to that account.
 *
 * The number is last because it is the one thing that MOVES: a renumber (the builder ↔
 * owner swap, the office's own "-N" convention) leaves both QB ids and QB names where
 * they were, so a number-first match crosses the accounts over. A journal line for
 * "5977AS - (CLSD) Epcon Communities" must reach the closed builder, not the live owner
 * who now holds "5977AS".
 */

import { parseCustomerName } from '@/utils/iif-customer-row';
import { parseNameFlags } from '@/utils/parse-name-flags';

/** A QB customer name as a lookup key: flag tokens stripped, case/whitespace-insensitive. */
export const qbNameKey = (name: string): string =>
	parseNameFlags(name.trim()).name.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Words that say nothing about WHO the owner is: entity/estate boilerplate, honorifics,
 * connectives and the close markers. "Smith Family Trust" and "Jones Family Trust"
 * share two words and no owner (Codex P1).
 */
const NON_DISTINCTIVE = new Set([
	'family', 'trust', 'trustee', 'trustees', 'revocable', 'irrevocable', 'living', 'estate', 'llc', 'inc', 'corp', 'co',
	'company', 'the', 'and', 'of', 'etc', 'mr', 'mrs', 'ms', 'jr', 'sr', 'closed', 'clsd',
]);

/** The owner words left once unit number, close marker, flags and punctuation are gone. */
const ownerWords = (name: string): string[] => {
	const { remainder } = parseCustomerName(parseNameFlags(name).name);
	// Unit numbers ("5977AS", "5977AS-1") and date parts ("4/21/2023") are not names.
	return remainder.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length >= 2 && !/^\d+[a-z]*$/.test(w));
};

/**
 * Do two names point at the same owner? Compares DISTINCTIVE words only: every one on
 * the side with fewer must appear on the other — "Wilt" ⊂ "Dennis & June Wilt" agrees,
 * "742PS - Doe" and "742PS - (CLOSED) Doe" agree; "Smith Family Trust" vs "Jones Family
 * Trust" ({smith} vs {jones}) and "Epcon Communities" vs "Epcon-Carr Farms" do not.
 * Unknown on either side (no owner words at all) agrees; owner words that are ALL
 * boilerplate ("The Family Trust") identify nobody, so they never agree.
 */
export const ownerAgrees = (stored: string | null | undefined, incoming: string | null | undefined): boolean => {
	if (!stored?.trim() || !incoming?.trim()) return true;
	const rawA = ownerWords(stored);
	const rawB = ownerWords(incoming);
	if (rawA.length === 0 || rawB.length === 0) return true;
	const a = new Set(rawA.filter((w) => !NON_DISTINCTIVE.has(w)));
	const b = new Set(rawB.filter((w) => !NON_DISTINCTIVE.has(w)));
	if (a.size === 0 || b.size === 0) return false;
	const [fewer, more] = a.size <= b.size ? [a, b] : [b, a];
	for (const w of fewer) if (!more.has(w)) return false;
	return true;
};

export interface IdentityAccount {
	id: string;
	accountNumber: string | null;
	externalId: string | null;
	externalName: string | null;
	/**
	 * Who the account's stored owner is, for the number fallback's agreement test:
	 * `external_name`, else whatever names the account (an auto-created account's
	 * address holds the QB name it was made for).
	 */
	ownerText?: string | null;
}

export interface IdentityQuery {
	externalId?: string | null;
	externalName?: string | null;
	accountNumber?: string | null;
	/** The incoming owner name for the agreement test; defaults to `externalName`. */
	ownerName?: string | null;
}

export type IdentityResolution =
	| { kind: 'match'; accountId: string; via: 'external_id' | 'external_name' | 'number' }
	| { kind: 'conflict'; accountId: string; reason: string }
	| { kind: 'none' };

/** The three lookups the rule needs — an index, or an importer's own live maps. */
export interface IdentityLookups {
	byExternalId(externalId: string): IdentityAccount | undefined;
	/** `null` when the name is shared by more than one account (identifies nothing). */
	byNameKey(key: string): IdentityAccount | null | undefined;
	byNumber(accountNumber: string): IdentityAccount | undefined;
}

export const resolveAccountIdentity = (q: IdentityQuery, lookups: IdentityLookups): IdentityResolution => {
	if (q.externalId) {
		const a = lookups.byExternalId(q.externalId);
		if (a) return { kind: 'match', accountId: a.id, via: 'external_id' };
	}
	if (q.externalName) {
		const a = lookups.byNameKey(qbNameKey(q.externalName));
		if (a && !(q.externalId && a.externalId && a.externalId !== q.externalId)) {
			return { kind: 'match', accountId: a.id, via: 'external_name' };
		}
	}
	if (q.accountNumber) {
		const a = lookups.byNumber(q.accountNumber.trim());
		if (a) {
			const sameQbId = !!q.externalId && a.externalId === q.externalId;
			const stored = a.externalName ?? a.ownerText ?? null;
			if (!sameQbId && !ownerAgrees(stored, q.ownerName ?? q.externalName)) {
				return {
					kind: 'conflict',
					accountId: a.id,
					reason: `QuickBooks customer ${q.accountNumber} now maps to a different app account `
						+ `(${q.accountNumber} belongs to "${stored}") — skipped; that account was not changed`,
				};
			}
			return { kind: 'match', accountId: a.id, via: 'number' };
		}
	}
	return { kind: 'none' };
};

/** A read-only index over an association's accounts, for importers that don't renumber. */
export class AccountIdentityIndex implements IdentityLookups {
	private readonly ext = new Map<string, IdentityAccount>();
	private readonly names = new Map<string, IdentityAccount | null>();
	private readonly numbers = new Map<string, IdentityAccount>();

	constructor(accounts: readonly IdentityAccount[]) {
		for (const a of accounts) this.add(a);
	}

	add(a: IdentityAccount): void {
		if (a.externalId && !this.ext.has(a.externalId)) this.ext.set(a.externalId, a);
		if (a.externalName) {
			const key = qbNameKey(a.externalName);
			const prior = this.names.get(key);
			this.names.set(key, prior === undefined || prior?.id === a.id ? a : null);
		}
		if (a.accountNumber && !this.numbers.has(a.accountNumber.trim())) this.numbers.set(a.accountNumber.trim(), a);
	}

	byExternalId(externalId: string): IdentityAccount | undefined { return this.ext.get(externalId); }

	byNameKey(key: string): IdentityAccount | null | undefined { return this.names.get(key); }

	byNumber(accountNumber: string): IdentityAccount | undefined { return this.numbers.get(accountNumber); }

	resolve(q: IdentityQuery): IdentityResolution { return resolveAccountIdentity(q, this); }
}

// Same shape as utils/shortcode.ts's SHORTCODE_PATTERN: "748PS", "4769SA".
const SHORTCODE_RE = /(\d+)([A-Z]{2,3})/;

/**
 * Resolve a QuickBooks customer NAME as it appears on a transaction ("5977AS - (CLSD)
 * Epcon Communities", "(c) 5755AV - Carr Farms") — the journal CSV import and the AR
 * enrichment matcher. Tries the name, then the unit number at the head of the name,
 * then any shortcode in it, each through `resolveAccountIdentity` (so a number whose
 * stored owner is someone else is a CONFLICT, not a match). Only a name with no
 * identity at all falls back to `addressShortcodes` — shortcodes in the address of
 * accounts that have no account number (auto-created from a QB name).
 */
export const resolveQbCustomerName = (
	index: IdentityLookups,
	qbName: string,
	addressShortcodes?: ReadonlyMap<string, string>,
): IdentityResolution | { kind: 'match'; accountId: string; via: 'address' } => {
	const head = parseCustomerName(parseNameFlags(qbName).name).accountNumber;
	const shortcode = qbName.match(SHORTCODE_RE);
	const numbers = [...new Set([
		head && /^\d+[A-Za-z]+(-\d+)?$/.test(head) ? head : undefined,
		shortcode ? `${shortcode[1]}${shortcode[2]}` : undefined,
	].filter((n): n is string => !!n))];
	let resolved: IdentityResolution = resolveAccountIdentity({ externalName: qbName, accountNumber: numbers[0] }, index);
	if (resolved.kind === 'none' && numbers[1]) {
		resolved = resolveAccountIdentity({ externalName: qbName, accountNumber: numbers[1] }, index);
	}
	if (resolved.kind !== 'none') return resolved;
	const viaAddress = shortcode ? addressShortcodes?.get(`${shortcode[1]}${shortcode[2]}`) : undefined;
	return viaAddress ? { kind: 'match', accountId: viaAddress, via: 'address' } : { kind: 'none' };
};

/** The account fields an app-side importer has (from GET /accounts). */
export interface ListedAccount {
	id: string;
	accountNumber?: string | null;
	externalId?: string | null;
	externalName?: string | null;
}

export const identityIndexOf = (accounts: readonly ListedAccount[]): AccountIdentityIndex =>
	new AccountIdentityIndex(accounts.map((a) => ({
		id: a.id, accountNumber: a.accountNumber ?? null, externalId: a.externalId ?? null, externalName: a.externalName ?? null,
	})));

/**
 * A starting-balances (Customer Balance template) row: matched by unit number, but only
 * when the account's stored QuickBooks owner agrees with the row's first/last name. A
 * file prepared before a #645 renumber that puts the BUILDER's balance on "5977AS" is a
 * conflict, not a charge on the live owner who now holds that number.
 */
export const resolveStartingBalanceRow = (
	index: AccountIdentityIndex,
	row: { accountNumber: string; firstName?: string | null; lastName?: string | null },
): IdentityResolution => index.resolve({
	accountNumber: row.accountNumber,
	ownerName: [row.firstName, row.lastName].filter(Boolean).join(' ') || null,
});
